import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getValidAuthToken, decodeToken, clearAuthData } from '@/utils/auth';

type Props = {
  requiredScopes?: string[];
  requiredAuth?: boolean;
  requiredMaster?: boolean;
  children: ReactNode;
  redirectToLogin?: boolean;
};

export const ProtectedRoute = ({
  requiredScopes = [],
  requiredAuth,
  requiredMaster,
  children,
  redirectToLogin = true,
}: Props) => {
  const location = useLocation();

  // Preserve query params (especially ?tenant=xxx) when redirecting to login
  const loginUrl = `/login${location.search}`;

  if (!requiredAuth) {
    return <>{children}</>;
  }

  // Get valid (non-expired) token - returns null if expired or missing
  const token = getValidAuthToken();

  if (!token) {
    // Token is missing or expired - redirect to login immediately
    return <Navigate to={loginUrl} replace state={{ form: location, toastUnauthorized: false }} />;
  }

  const decoded = decodeToken(token);

  if (!decoded) {
    // Token is malformed - clear and redirect
    clearAuthData();
    if (redirectToLogin) {
      return <Navigate to={loginUrl} replace state={{ form: location, toastUnauthorized: true }} />;
    }
    return <>{children}</>;
  }

  if (requiredMaster && decoded.isMaster !== true) {
    return <Navigate to="/" replace />;
  }

  const userScopes = decoded.scope ?? [];
  const hasAllScopes = requiredScopes.every((scope) => userScopes.includes(scope));

  if (!hasAllScopes) {
    if (redirectToLogin) {
      return <Navigate to={loginUrl} replace state={{ form: location, toastUnauthorized: true }} />;
    }
  }

  return <>{children}</>;
};
